import type { Language } from './language';

/**
 * Форматирование чисел, дат и размеров средствами `Intl` (§11, фаза 10,
 * задача 7).
 *
 * Собрано отдельным модулем, а не разбросано по вызовам `toLocaleString`,
 * по одной причине: локаль должна браться из выбранного языка, а не из
 * настроек браузера. Инженер с русским интерфейсом на английской системе
 * ждёт `01.09.2026`, а не `09/01/2026`, — и наоборот. До фазы 10 локаль
 * `ru-RU` стояла в коде строкой, и переключение языка её бы не тронуло.
 *
 * Форматтеры кешируются: `Intl.DateTimeFormat` — дорогой конструктор,
 * а таблица журнала строит его на каждую из пятисот строк.
 */

/** Локаль `Intl` для языка интерфейса. */
const LOCALES: Readonly<Record<Language, string>> = {
  ru: 'ru-RU',
  en: 'en-US',
};

export function localeOf(language: string): string {
  return LOCALES[language as Language] ?? LOCALES.ru;
}

const dateTimeCache = new Map<string, Intl.DateTimeFormat>();
const numberCache = new Map<string, Intl.NumberFormat>();

export function dateTimeFormat(
  language: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const locale = localeOf(language);
  const key = `${locale}|${JSON.stringify(options)}`;

  let formatter = dateTimeCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateTimeCache.set(key, formatter);
  }

  return formatter;
}

export function numberFormat(
  language: string,
  options: Intl.NumberFormatOptions = {},
): Intl.NumberFormat {
  const locale = localeOf(language);
  const key = `${locale}|${JSON.stringify(options)}`;

  let formatter = numberCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    numberCache.set(key, formatter);
  }

  return formatter;
}
